import { FeatureAppDefinition, FeatureServices } from '@feature-hub/core';
import { ReactFeatureApp } from '@feature-hub/react';
import React from 'react';
import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject
} from '@apollo/client';
import isBrowser from 'is-browser';
import fetch from 'cross-fetch';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { App } from './app';
import introspectionQueryResultData from './generated/fragment-types';

type FeatureAppConfig = {
  environment?: string;
};

type Maybe<T> = T | undefined;

const environmentHeaderLink = (environment: Maybe<string>) =>
  new ApolloLink((operation, forward) => {
    if (!forward) {
      throw new Error('EnvironmentHeaderLink cannot be the last link in the chain.');
    }

    if (!environment) {
      return forward(operation);
    }

    operation.setContext((currentContext: Record<string, object>) => ({
      ...currentContext,
      headers: {
        ...currentContext.headers,
        'x-e-charge-env': environment
      }
    }));

    return forward(operation);
  });

const featureAppDefinition: FeatureAppDefinition<
  ReactFeatureApp,
  FeatureServices,
  FeatureAppConfig
> = {
  dependencies: {
    featureServices: {
      's2:serialized-state-manager': '^1.0.0'
    }
  },
  optionalDependencies: {
    featureServices: { 's2:async-ssr-manager': '^1.0.0' }
  },
  create: env => {
    if (!env.baseUrl) {
      throw new Error("baseUrl isn't set");
    }

    const environment = env.config?.environment;

    const serializedStateManager = env.featureServices[
      's2:serialized-state-manager'
    ] as SerializedStateManagerV1;

    const apolloCache = new InMemoryCache({
      possibleTypes: introspectionQueryResultData.possibleTypes
    });

    if (isBrowser) {
      const apolloState = JSON.parse(
        serializedStateManager.getSerializedState() ?? '{}'
      )?.apolloState;

      if (apolloState) {
        apolloCache.restore(apolloState);
      }
    }

    const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
      ssrMode: !isBrowser,
      cache: apolloCache,
      link: environmentHeaderLink(environment).concat(
        new HttpLink({ fetch, uri: `${env.baseUrl}/bff` })
      )
    });

    if (!isBrowser) {
      serializedStateManager.register(() => JSON.stringify({ apolloState: client.extract() }));
    }

    return {
      render: () => (
        <ApolloProvider client={client}>
          <App />
        </ApolloProvider>
      )
    };
  }
};

export default featureAppDefinition;
